import { AsyncFn, EventHook, Log } from '../../common'
import { CompanyGuid, ForCompany } from '../Company/Company'
import { BreezySystemUserGuid, UserGuid } from '../Users/User'

export const FEATURE_FLAGS = [
  'testFlag',
  'billingAddresses', // delete this flag
  'newTechAppHeader', // Delete this flag, never enabled in production
  'platformVersionMismatchNotification',
  'integratedCalling',
  'assistant-settings',
  'flat-rate-pricing', // Controls Service Includes UI (linking labor/materials/equipment to services)
  'pricebook-markups', // Controls visibility of pricebook markup tiers feature
  'disable-hiding-mp-invoices-from-invoice-list',
  'embedded-reporting',
  'job-costing',
  'integrated-messaging',
  'estimate-rehash-ai',
  'statements',
  'estimate-pdf-service',
  'maintenance-plan-performance',
  'timesheet-csv-export',
  'qbd-location-subaccounts-rollout',
  'tilled-account-anchor-enforcement',
] as const

export const QBD_LOCATION_SUBACCOUNTS_ROLLOUT_FLAG = 'qbd-location-subaccounts-rollout'
export const TILLED_ACCOUNT_ANCHOR_ENFORCEMENT_FLAG = 'tilled-account-anchor-enforcement'

export type FeatureFlag = (typeof FEATURE_FLAGS)[number]

export type FeatureFlags = Record<FeatureFlag, boolean>

export type FeatureFlagReader = <T extends FeatureFlag>(
  key: T,
  companyGuid: CompanyGuid | undefined,
  userGuid: UserGuid | typeof BreezySystemUserGuid,
) => Promise<boolean>

export type FeatureFlagClient = {
  read: FeatureFlagReader
  close: () => void
}

export type FeatureFlaggedForCompany<T, TOutput = void, TInput = ForCompany<T>, Fn = AsyncFn<TInput, TOutput>> = {
  featureFlag: FeatureFlag
  featureFlagReader: FeatureFlagReader
  enabledFn: Fn
  disabledFn: Fn
  logFlagValues?: boolean
}

export const createFeatureFlaggedForCompany = <TInput, TOutput = void>({
  featureFlag,
  featureFlagReader,
  enabledFn,
  disabledFn,
  logFlagValues = true,
}: FeatureFlaggedForCompany<TInput, TOutput>): AsyncFn<ForCompany<TInput>, TOutput> => {
  return async input => {
    let featureFlagEnabled: boolean
    try {
      featureFlagEnabled = await featureFlagReader(featureFlag, input.companyGuid, BreezySystemUserGuid)
      if (logFlagValues) {
        Log.info('Feature flag value', { featureFlag, featureFlagEnabled, companyGuid: input.companyGuid })
      }
    } catch (err) {
      Log.warn('Could not read feature flag. Defaulting to disabled.', { featureFlag, input })
      featureFlagEnabled = false
    }

    if (featureFlagEnabled) {
      return await enabledFn(input)
    }

    return await disabledFn(input)
  }
}

export type FeatureFlagEventWriterConfig = {
  featureFlag: FeatureFlag
  featureFlagReader: FeatureFlagReader
  enabledWriter: EventHook
  disabledWriter?: EventHook
}

export const createFeatureFlaggedEventWriter = ({
  featureFlag,
  featureFlagReader,
  enabledWriter,
  disabledWriter = async _ => {},
}: FeatureFlagEventWriterConfig): EventHook => {
  return async input => {
    try {
      const featureFlagEnabled = await featureFlagReader(featureFlag, input.companyGuid, input.actingUserGuid)

      if (featureFlagEnabled) {
        return await enabledWriter(input)
      }
      return await disabledWriter(input)
    } catch (err) {
      Log.warn('Could not read feature flag', { featureFlag, input })
      return await disabledWriter(input)
    }
  }
}

export const UNAUTH_USER_KEY = 'unauthenticated'
